"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MeowPlayer = void 0;
const log_client_1 = require("@video/log-client");
const adapter_1 = require("../../../api/adapter");
const common_1 = require("../../../utils/common");
const errors_deprecated_1 = require("../../errors-deprecated");
const context_1 = require("../../utils/context/context");
const instance_collector_1 = require("../../utils/debug/instance-collector");
const event_emitter_1 = require("../../utils/events/event-emitter");
const logger_1 = require("../../utils/logger");
const helper_1 = require("../helper");
const mp4_ws_1 = require("../mp4-ws");
const STORAGE_VALUES = {
    muted: "lv_muted",
    volume: "lv_volume",
    lockedKbps: "lv_locked_kbps", // [null]
    estimatedKbps: "lv_estimated_kbps", // [1800]
    autoLastKbps: "lv_auto_last_kbps", // [1800]
    hideSuggestion: "lv_hide_suggestion",
    origin: "lv_origin",
    profile: "lv_origin",
};
const PLAYBACK_MAX_LATENCY = 25;
const DELTA = 0.0001;
// we don't have types for Buffer and ArrayBuffer types for now
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sbMeta = new WeakMap();
class MeowPlayer extends event_emitter_1.ObservableEventEmitter {
    constructor(ctx, player, options, el) {
        var _a, _b;
        super();
        this.manifest = null;
        this.buffering = false;
        this.queue = [];
        this.sourceBuffers = [];
        this.receivedSegments = 0;
        this.abrController = null;
        this.encoding = null;
        this.transport = null;
        this.version = null;
        this.ms = null;
        this.mediaSourceTime = null;
        this.lastMessageReceived = null;
        this.logInterval = undefined;
        this.gcInterval = undefined;
        this.destroyed = false;
        this.ctx = ctx;
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`Mp4ws Context Cancelled: ${reason}`));
        this.addInnerDisposer(this.destroy);
        if (!adapter_1.device.isImplements(adapter_1.Feature.DEBUGGING) ||
            !adapter_1.device.isImplements(adapter_1.Feature.MEDIA_SOURCE) ||
            !adapter_1.device.isImplements(adapter_1.Feature.LOCAL_STORAGE)) {
            throw new Error("Device is not supported");
        }
        this.device = adapter_1.device;
        this.videoPlayer = player;
        this.options = options;
        this.el = el;
        // set up the ABR Controller
        if (this.options.bitrate == null) {
            const profile = (_a = this.device.localStorage.getItem(STORAGE_VALUES.profile)) !== null && _a !== void 0 ? _a : "normal";
            const abrController = new AbrController((0, logger_1.extendContext)(ctx, AbrController), this, this.videoPlayer, profile, false, (_b = this.options.maxShifts) !== null && _b !== void 0 ? _b : Infinity, this.options);
            this.abrController = abrController;
            abrController.on("profile", (data) => {
                // bug: this.videoPlayer.store doesn't use TTL
                // const ttl = detect.any() ? 6 * 1000 * 60 * 60 : 1000 * 60 * 60 * 24 * 60;
                this.device.localStorage.setItem(STORAGE_VALUES.estimatedKbps, data.estimatedKbps.toString() /* , ttl */);
                this.device.localStorage.setItem(STORAGE_VALUES.profile, data.profile.key /* , ttl */);
            });
            this.abrController.on("upshift", () => {
                this.emit("upshift");
            });
            this.abrController.on("downshift", () => {
                this.emit("downshift");
            });
        }
        let encoding;
        if (this.options.origin && this.videoPlayer.data != null) {
            encoding = this.videoPlayer.data.origin;
        }
        else {
            encoding = this.videoPlayer.pickEncoding();
        }
        if (encoding == null && this.videoPlayer.data != null && this.videoPlayer.data.encodings.length > 0) {
            [encoding] = this.videoPlayer.data.encodings;
        }
        if (encoding != null) {
            this.encoding = encoding;
        }
        this.setupMSE();
    }
    get readyState() {
        var _a;
        if (this.ms == null) {
            return null;
        }
        const { encoding } = this;
        if (encoding == null || ((_a = encoding.channels) === null || _a === void 0 ? void 0 : _a.length) !== this.sourceBuffers.length) {
            return null;
        }
        return this.ms.readyState;
    }
    appendBuffer() {
        if (this.buffering) {
            return;
        }
        const wait = () => {
            this.buffering = true;
            adapter_1.device.setTimeout(() => {
                this.buffering = false;
                this.appendBuffer();
            }, 20);
        };
        if (this.readyState !== "open") {
            wait();
            return;
        }
        const op = this.queue.shift();
        if (op == null) {
            wait();
            return;
        }
        this.buffering = true;
        if (op.type === "remove") {
            if (op.sb == null) {
                this.buffering = false;
                this.appendBuffer();
                return;
            }
            this.ctx.logger.debug("remove buffer", {
                buffer: `${op.start}-${op.end} buffered until ${op.sb.buffered.end(0)}`,
            });
            op.sb.remove(op.start, op.end);
        }
        else {
            op.blob.arrayBuffer().then((ab) => {
                if (this.transport == null) {
                    throw new Error("Transport is not defined");
                }
                const currentSegmentInfo = this.transport.getSegmentInfo(ab);
                // backwards compat
                if (this.version === 1) {
                    currentSegmentInfo.contentTag = 0;
                }
                const ctsb = this.sourceBuffers[currentSegmentInfo.contentTag];
                this.emit("fragment", {
                    size: op.blob.size,
                    time: op.time,
                    duration: 300,
                });
                if (this.readyState !== "open") {
                    this.ctx.logger.debug("meow mse not ready", {
                        readyState: this.readyState,
                    });
                    this.queue.unshift(op);
                    wait();
                    return;
                }
                if (ctsb == null) {
                    this.queue.unshift(op);
                    wait();
                    return;
                }
                try {
                    ctsb.appendBuffer(ab);
                }
                catch (err) {
                    this.emitErrorDeprecated(new errors_deprecated_1.Mp4BufferError("failed to append source buffer in appendBuffer()", { inner: (0, errors_deprecated_1.wrapNativeError)(err) }));
                }
            });
        }
    }
    checkSourceBuffers() {
        var _a, _b, _c;
        for (const sourceBuffer of this.sourceBuffers) {
            if (((_a = sourceBuffer === null || sourceBuffer === void 0 ? void 0 : sourceBuffer.buffered.length) !== null && _a !== void 0 ? _a : 0) === 0) {
                return false;
            }
        }
        return ((_c = (_b = this.el) === null || _b === void 0 ? void 0 : _b.buffered.length) !== null && _c !== void 0 ? _c : 0) !== 0;
    }
    newSourceBuffer(mimetype) {
        if (mimetype == null || !this.device.MediaSource.isTypeSupported(mimetype)) {
            this.sourceBuffers.push(null);
            return;
        }
        if (this.ms == null) {
            return;
        }
        const sb = this.ms.addSourceBuffer(mimetype);
        this.ctx.logger.debug("meow adding sourcebuffer", { mimetype });
        sbMeta.set(sb, { mimetype });
        sb.addEventListener("error", (err) => {
            this.emitErrorDeprecated(new errors_deprecated_1.Mp4BufferError("failed to append source buffer in newSourceBffer()", { inner: err }));
            this.buffering = false;
            this.appendBuffer();
        });
        sb.addEventListener("updateend", () => {
            var _a, _b, _c;
            this.buffering = false;
            this.appendBuffer();
            const { el } = this;
            if (el == null) {
                return;
            }
            if (el.buffered.length > 0) {
                const playbackDifference = el.buffered.end(el.buffered.length - 1) - el.currentTime;
                if (el.currentTime === 0) {
                    const bufferDifference = el.buffered.end(el.buffered.length - 1) - el.buffered.start(0);
                    if (bufferDifference > ((_a = this.options.requiredBuffer) !== null && _a !== void 0 ? _a : 0)) {
                        const startTime = Math.max(el.buffered.end(el.buffered.length - 1) - ((_b = this.options.requiredBuffer) !== null && _b !== void 0 ? _b : 0), el.buffered.start(0));
                        el.currentTime = startTime;
                        this.ctx.logger.debug("meow attempting to start", {
                            startTime,
                        });
                        this.play();
                    }
                }
                else if (playbackDifference > PLAYBACK_MAX_LATENCY) {
                    const ct = el.currentTime;
                    const nt = Math.max(el.buffered.end(el.buffered.length - 1) - ((_c = this.options.requiredBuffer) !== null && _c !== void 0 ? _c : 0), el.buffered.start(0));
                    el.currentTime = nt;
                    this.ctx.logger.warn("meow buffer max reached, moving to playback head", {
                        max: PLAYBACK_MAX_LATENCY,
                        ct,
                        nt,
                    });
                }
            }
            this.receivedSegments++;
            if (this.receivedSegments > 60 && !this.checkSourceBuffers()) {
                this.ctx.logger.error("no buffer after 60 segments");
                this.emit("error", {
                    message: "media-error",
                    fatal: true,
                    notSupported: true,
                });
                return;
            }
            /** logs */
            const bufferDurations = [];
            this.sourceBuffers.forEach((bsb, k) => {
                if (bsb == null || this.readyState !== "open") {
                    return;
                }
                for (let i = 0; i < bsb.buffered.length; i++) {
                    bufferDurations.push({
                        sourceBufferIndex: k,
                        index: i,
                        start: bsb.buffered.start(i),
                        end: bsb.buffered.end(i),
                    });
                }
            });
            if (bufferDurations.length > 0) {
                // this.ctx.logger.debug("meow buffer appended", {
                //   currentTime: this.el?.currentTime,
                //   bufferDurations,
                // });
            }
            /** end logs */
        });
        this.sourceBuffers.push(sb);
    }
    setupMSE() {
        this.ms = new this.device.MediaSource();
        this.ms.addEventListener("error", () => {
            this.ctx.logger.error("meow mediasource dom error");
            // if it errors, its because of a fatal decoding error
            this.emit("error", {
                message: "media-error",
                fatal: true,
            });
        });
        this.ms.addEventListener("abort", () => {
            this.ctx.logger.error("meow mediasource dom abort");
            this.emit("error", {
                message: "media-error",
                fatal: true,
            });
        });
        this.ms.addEventListener("sourceended", () => {
            this.ctx.logger.debug("meow mediasource source ended");
        });
        this.ms.addEventListener("sourceclose", () => {
            this.ctx.logger.debug("meow mediasource source closed");
        });
        this.ms.addEventListener("sourceopen", this.onSourceOpen);
        this.ctx.logger.debug("meow attempting to create media source");
        this.mediaSourceTime = this.device.performance.now();
        if (this.el != null) {
            // set the source so that the ready() check in the shared driver logic passes.
            if (this.videoPlayer instanceof mp4_ws_1.Mp4WsPlayer) {
                this.videoPlayer.source = URL.createObjectURL(this.ms);
                this.el.src = this.videoPlayer.source;
            }
            else {
                this.el.src = URL.createObjectURL(this.ms);
            }
        }
    }
    onSourceOpen() {
        this.ms.removeEventListener("sourceopen", this.onSourceOpen);
        this.ctx.logger.debug("meow media source opened");
        const { encoding } = this;
        if (encoding == null) {
            this.emit("error", {
                message: "media-error",
                fatal: true,
            });
            return;
        }
        try {
            if ((encoding === null || encoding === void 0 ? void 0 : encoding.channels) != null) {
                if (encoding.channels.length === 0) {
                    this.emit("error", {
                        message: "media-error",
                        fatal: true,
                        notSupported: true,
                    });
                    return;
                }
                this.version = 2;
                for (const chan of encoding.channels) {
                    this.newSourceBuffer(chan);
                }
            }
            else {
                this.version = 1;
                const { origin } = this.videoPlayer.data;
                let mimetype;
                if ((this.options.origin != null && origin != null && origin.audioCodec !== "aac") ||
                    (!this.options.origin && this.videoPlayer.data.audioCodec !== "aac")) {
                    mimetype = "video/mp4; codecs=\"avc1.4d4028\"";
                }
                else {
                    mimetype = "video/mp4; codecs=\"avc1.4d4028, mp4a.40.2\"";
                }
                this.ctx.logger.debug("meow adding sourcebuffer", { mimetype });
                this.newSourceBuffer(mimetype);
            }
        }
        catch (err) {
            const inner = err instanceof Error ? err : null;
            this.emitErrorDeprecated(new errors_deprecated_1.MeowDriverError("meow error in newSourceBuffer(). restarting player", {
                inner,
            }));
            this.destroy();
            if (this.videoPlayer instanceof mp4_ws_1.Mp4WsPlayer) {
                this.videoPlayer.player = null;
                this.videoPlayer.restart(false);
            }
        }
    }
    setupTransport() {
        var _a, _b, _c, _d, _e, _f;
        const channelCount = ((_c = (_b = (_a = this.encoding) === null || _a === void 0 ? void 0 : _a.channels) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0 ? (_f = (_e = (_d = this.encoding) === null || _d === void 0 ? void 0 : _d.channels) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0 : 1;
        const { manifest } = this.videoPlayer;
        this.manifest = manifest;
        if (manifest == null) {
            this.emit("error", { message: helper_1.ERRORS.INVALID_MEDIA_URL, fatal: true });
            return;
        }
        if (this.transport != null) {
            this.transport.removeAllListeners();
            this.transport.destroy();
        }
        this.transport = new Transport((0, logger_1.extendContext)(this.ctx, Transport), manifest, this.options.backFill, this.options.maxGap, this.options.retry, this.options.mode, this.options.download, this.options.segmentDuration, channelCount);
        this.transport.on("error", () => {
            var _a;
            if ((_a = this.transport) === null || _a === void 0 ? void 0 : _a.disconnected) {
                this.emit("error", { message: helper_1.ERRORS.WS_NETWORK_ERROR, fatal: false });
            }
            else {
                this.emit("error", {
                    message: "websocket-connection",
                    notSupported: true,
                });
            }
        });
        this.transport.on("destroy", () => {
            this.emit("error", { message: "websocket-connection", fatal: true });
        });
        this.transport.on("message", (ev) => {
            this.lastMessageReceived = this.device.performance.now();
            this.queue.push({
                type: "append",
                blob: ev.data,
                time: ev.fetchTime,
            });
            this.appendBuffer();
        });
    }
    switchBitrate(name) {
        var _a;
        this.ctx.logger.debug("meow switching bitrate to ", {
            name,
        });
        // not sure why we have to destroy abrController after first bitrate switching?
        // this.abrController?.destroy();
        (_a = this.transport) === null || _a === void 0 ? void 0 : _a.switchBitrate(name);
    }
    async play() {
        return new Promise((resolve, reject) => {
            const { el } = this;
            if (el == null) {
                reject(new Error(helper_1.ERRORS.ELEMENT_REQUIRED));
                return;
            }
            this.queue = [];
            if (this.transport == null) {
                this.setupTransport();
            }
            this.lastMessageReceived = this.device.performance.now();
            clearInterval(this.logInterval);
            this.logInterval = adapter_1.device.setInterval(() => {
                var _a;
                const diff = this.device.performance.now() - ((_a = this.lastMessageReceived) !== null && _a !== void 0 ? _a : 0);
                if (diff > 2000) {
                    this.emit("error", {
                        message: "stall",
                        timeSinceLastFrag: diff,
                    });
                }
            }, 1000);
            clearInterval(this.gcInterval);
            this.gcInterval = adapter_1.device.setInterval(() => {
                if ((el === null || el === void 0 ? void 0 : el.currentTime) == null) {
                    return;
                }
                for (const sb of this.sourceBuffers) {
                    this.queue.push({
                        type: "remove",
                        sb: sb !== null && sb !== void 0 ? sb : undefined,
                        start: 0,
                        end: el.currentTime - 15,
                    });
                }
            }, 30 * 1000);
            if (this.videoPlayer instanceof mp4_ws_1.Mp4WsPlayer) {
                this.ctx.logger.info("meow player play attempted");
                this.videoPlayer.corePlay().then(() => resolve()).catch((e) => {
                    this.ctx.logger.warn("meow player play attempt failed", {
                        errName: e.name,
                        errMessage: e.message,
                    });
                    this.stop();
                    reject();
                });
            }
            else {
                el.play().then(resolve).catch(reject);
            }
        });
    }
    stop() {
        var _a;
        const { el } = this;
        if (el == null) {
            throw new Error(helper_1.ERRORS.ELEMENT_REQUIRED);
        }
        this.ctx.logger.debug("meow player stop");
        clearInterval(this.gcInterval);
        clearInterval(this.logInterval);
        (_a = this.transport) === null || _a === void 0 ? void 0 : _a.removeAllListeners("message");
        this.buffering = false;
        el.pause();
        if (el.src !== "") {
            this.ctx.logger.debug("meow revoking media source url");
            URL.revokeObjectURL(el.src);
            el.src = "";
        }
        this.emit("stop");
    }
    destroy() {
        var _a;
        this.destroyed = true;
        if (this.transport != null) {
            this.transport.removeAllListeners("destroy");
            this.transport.removeAllListeners("message");
            this.transport.destroy();
        }
        (_a = this.abrController) === null || _a === void 0 ? void 0 : _a.destroy();
        this.stop();
    }
}
exports.MeowPlayer = MeowPlayer;
MeowPlayer.displayName = "Mp4WsPlayer";
const BOX_NAMES = ["moof", "mfhd"];
const INIT_BOX = "ftyp";
function sFCC(dv, n) {
    return String.fromCharCode(dv.getUint8(n));
}
function getContentTag(ab, dv) {
    if (ab.byteLength < 9) {
        return 1;
    }
    // last box should be skip
    if (dv.getUint32(ab.byteLength - 5) !== 0x736b6970) {
        return 1;
    }
    return dv.getUint8(ab.byteLength - 1);
}
class Latency {
    constructor() {
        this.lastTime = adapter_1.device.isImplements(adapter_1.Feature.DEBUGGING) ? adapter_1.device.performance.now() : Date.now();
        this.lastSegmentTime = adapter_1.device.isImplements(adapter_1.Feature.DEBUGGING)
            ? adapter_1.device.performance.now()
            : Date.now();
        this.lastSegmentDuration = 0;
    }
}
class Transport extends event_emitter_1.ObservableEventEmitter {
    /**
     * @param uri
     * @param {int} backFill
     * @param {int} maxGap
     * @param {int} retry
     * @param mode
     * @param download
     * @param segmentDuration
     * @param channelCount
     */
    constructor(ctx, uri, backFill = 0, maxGap = 500, retry = 20, mode = "B", download = false, segmentDuration = 300, channelCount = 1) {
        super();
        this.downloadedSegments = [];
        this.introFrag = {};
        this.channelTracker = 0;
        this.channelLatency = [];
        this.pingInterval = undefined;
        this.ctx = ctx;
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`Transport Class Context Cancelled: ${reason}`));
        this.addInnerDisposer(this.destroy);
        if (!adapter_1.device.isImplements(adapter_1.Feature.DEBUGGING) ||
            !adapter_1.device.isImplements(adapter_1.Feature.MEDIA_SOURCE) ||
            !adapter_1.device.isImplements(adapter_1.Feature.LOCAL_STORAGE)) {
            throw new Error("Device is not supported");
        }
        this.device = adapter_1.device;
        this.uri = uri;
        this.backFill = backFill;
        this.maxGap = maxGap;
        this.retry = retry;
        this.mode = mode;
        this.segmentDuration = segmentDuration;
        this.saveToDownload = download;
        this.introFrag = {};
        const url = `${this.uri}&bckfil=${this.backFill}`;
        this.ws = new WebSocket(url);
        instance_collector_1.InstanceCollector.reportNewInstance("websocket", this.ws, { file: "player/mp4-ws.ts" });
        this.channelLatency = [];
        for (let i = 0; i < channelCount; i++) {
            this.channelLatency.push(new Latency());
        }
        this.ctx.logger.debug("meow ws connecting", { url });
        this.ws.onopen = () => {
            this.ctx.logger.debug("meow ws open");
        };
        this.ws.onmessage = (ev) => {
            var _a;
            // this.ctx.logger.debug("meow ws frame received", { datatype: typeof ev.data });
            if (typeof ev.data === "object") {
                this.emit("message", {
                    ...ev,
                    data: ev.data,
                    fetchTime: this.device.performance.now() - this.lastTime + 10,
                    segmentDuration: (_a = this.lastSegmentDuration) !== null && _a !== void 0 ? _a : this.segmentDuration,
                });
                this.lastSegmentTime = this.device.performance.now();
                if (this.saveToDownload) {
                    this.downloadedSegments.push(ev.data);
                }
                this.nextChannel();
                return;
            }
            const parts = ev.data.split(/\s/g);
            const mSegmentDuration = parseInt(parts[0], 10);
            if (Number.isNaN(mSegmentDuration)) {
                try {
                    this.introFrag = JSON.parse(ev.data);
                }
                catch (err) {
                    this.ctx.logger.error("unhandled websocket frame - attempted to parse and failed", { event: ev.data });
                }
                return;
            }
            this.lastTime = this.device.performance.now();
            this.lastSegmentDuration =
                mSegmentDuration < 2000 && mSegmentDuration > 0 ? mSegmentDuration : this.segmentDuration;
        };
        this.ws.onclose = (code) => {
            this.ctx.logger.debug("meow ws closing", { reason: code.reason, code: code.code });
            instance_collector_1.InstanceCollector.disposeInstance("websocket", this.ws);
            this.emit("close", { code });
            this.emit("destroy");
        };
        this.ws.onerror = () => {
            this.emit("error");
        };
        ctx.logger.attachObject(this);
        ctx.logger.trace("constructor()");
    }
    get lastTime() {
        return this.channelLatency[this.channelTracker].lastTime;
    }
    set lastTime(v) {
        this.channelLatency[this.channelTracker].lastTime = v;
    }
    get lastSegmentTime() {
        return this.channelLatency[this.channelTracker].lastSegmentTime;
    }
    set lastSegmentTime(v) {
        this.channelLatency[this.channelTracker].lastSegmentTime = v;
    }
    get lastSegmentDuration() {
        return this.channelLatency[this.channelTracker].lastSegmentDuration;
    }
    set lastSegmentDuration(v) {
        this.channelLatency[this.channelTracker].lastSegmentDuration = v;
    }
    get disconnected() {
        return this.ws.readyState > 1;
    }
    nextChannel() {
        this.channelTracker++;
        if (this.channelLatency.length >= this.channelTracker) {
            this.channelTracker = 0;
        }
    }
    download() {
        // const a = document.createElement("a");
        // document.body.appendChild(a);
        // a.style.display = "none";
        //
        // const blob = new window.Blob(this.downloadedSegments, {
        //   type: "octet/stream",
        // });
        // const url = window.URL.createObjectURL(blob);
        // a.href = url;
        // a.download = "blah.mp4";
        // a.click();
        // window.URL.revokeObjectURL(url);
    }
    // get latency() {
    // 	if (!this.latencyCount) {
    // 		return 0;
    // 	}
    // 	return this.latencySum / this.latencyCount * 0.75;
    // }
    /**
     * parse out current segment number from skip boxes
     * @return {int} current segment number
     */
    getSegmentInfo(ab) {
        const dv = new DataView(ab);
        const contentTag = getContentTag(ab, dv);
        let boxSize = 0;
        let i = 0;
        let j = 0;
        let len = ab.byteLength;
        let segmentNo = 0;
        let init = false;
        if (`${sFCC(dv, 4)}${sFCC(dv, 5)}${sFCC(dv, 6)}${sFCC(dv, 7)}` === INIT_BOX) {
            init = true;
        }
        for (i = 0; i < len && j < BOX_NAMES.length;) {
            boxSize = dv.getUint32(i);
            // step into box
            if (sFCC(dv, i + 4) === BOX_NAMES[j].charAt(0) &&
                sFCC(dv, i + 5) === BOX_NAMES[j].charAt(1) &&
                sFCC(dv, i + 6) === BOX_NAMES[j].charAt(2) &&
                sFCC(dv, i + 7) === BOX_NAMES[j].charAt(3)) {
                len = i + boxSize;
                j++;
                i += 8; // works only for Box, not for extended box
            }
            else {
                i += boxSize;
            }
        }
        // we found it
        if (j === BOX_NAMES.length) {
            segmentNo = dv.getUint32(i + 4);
        }
        return {
            contentTag,
            segment: segmentNo,
            init,
        };
    }
    /**
     * Switches the current active bitrate
     *
     * @param name stream name
     */
    switchBitrate(name) {
        this.ws.send(`switch_stream?name=${name}&mode=${this.mode}&bckfil=${this.backFill}&retry=${this.retry}&mxgap=${this.maxGap}`);
    }
    /**
     * Destroys the websocket connection
     */
    destroy() {
        this.ctx.logger.info("meow transport destroy");
        this.downloadedSegments = [];
        clearInterval(this.pingInterval);
        if (this.ws == null) {
            return;
        }
        this.ctx.logger.info("meow ws close");
        this.ws.onerror = null;
        this.ws.close();
        instance_collector_1.InstanceCollector.disposeInstance("websocket", this.ws);
        this.dispose("Transport was destroyed");
    }
    toJSON() {
        return {
            uri: this.uri,
            disconnected: this.disconnected,
            retry: this.retry,
            aggregates: {
                support: this.ctx.support.hash,
                wsReadyState: this.ws.readyState,
            },
        };
    }
}
Transport.displayName = "Mp4WsTransport";
const SAMPLE_SIZE = 300; // sample size for estimating bandwidth
const RECENT_KBPS = 120;
const PROFILE_INTERVAL = 10000;
const RECENT_KBPS_INTERVAL = 1000;
const MAX_BW_ESTIMATE = 50000 * 8;
const BASE_SWITCH_THRESHOLD = 8 * 1000; // 8 seconds
const MIN_SIZE = 2000; // minimum size to use in estimated bw
const MIN_MS_TIME = 2; // minimum required ms download time
const IGNORE = 0;
const MIN_SAMPLE_SIZE = 25;
const EMERGENCY_RELEASE = 900;
const profileTypes = {
    stable: {
        key: "stable",
        bwModifier(bytes) {
            return Math.min(bytes, bytes - (bytes * 0.01 + Math.log(bytes * bytes) / Math.log(2)));
        },
        resistUpshift(kbps, encodingKbps) {
            return encodingKbps * 2.5 + Math.log(encodingKbps * 2) ** 3.2 > kbps * 2;
        },
        resistDownshift(kbps, encodingKbps) {
            return kbps * 2 + Math.log(kbps * 2) ** 3.2 > encodingKbps * 2;
        },
    },
    normal: {
        key: "normal",
        bwModifier(bytes) {
            return Math.min(bytes, bytes - (bytes * 0.06 + Math.log(bytes * bytes) / Math.log(2)));
        },
        resistUpshift(kbps, encodingKbps) {
            return encodingKbps * 2.5 + Math.log(encodingKbps * 2) ** 3.4 > kbps * 2;
        },
        resistDownshift(kbps, encodingKbps) {
            return kbps * 2 + Math.log(kbps * 2) ** 3.4 > encodingKbps * 2;
        },
    },
    uncertain: {
        key: "uncertain",
        bwModifier(bytes) {
            return Math.min(bytes, bytes - (bytes * 0.3 + Math.log(bytes * bytes) / Math.log(2)));
        },
        resistUpshift(kbps, encodingKbps) {
            return encodingKbps * 2.5 + Math.log(encodingKbps * 2) ** 3.6 > kbps * 2;
        },
        resistDownshift(kbps, encodingKbps) {
            return kbps * 2 + Math.log(kbps * 2) ** 3.4 > encodingKbps * 2;
        },
    },
    sporadic: {
        key: "sporadic",
        bwModifier(bytes) {
            return Math.min(bytes, bytes - (bytes * 0.5 + Math.log(bytes * bytes) / Math.log(2)));
        },
        resistUpshift(kbps, encodingKbps) {
            return encodingKbps * 2.5 + Math.log(encodingKbps * 2) ** 4 > kbps * 2;
        },
        resistDownshift(kbps, encodingKbps) {
            return kbps * 2 + Math.log(kbps * 2) ** 3.4 > encodingKbps * 2;
        },
    },
};
class AbrController extends event_emitter_1.ObservableEventEmitter {
    constructor(ctx, player, driver, profile, switchImmediately, maxShifts, options) {
        var _a, _b, _c;
        super();
        this.switchThreshold = BASE_SWITCH_THRESHOLD;
        this.count = 0;
        this.dropCount = 0;
        this.totalShifts = 0;
        this.rawKbps = [];
        this.recentKbps = [];
        this.sampleBws = [];
        this.selectedEncoding = null;
        this.emergencyModifier = 1;
        this.releaseEmergencyTimeout = undefined;
        this.profileInterval = undefined;
        this.trackRecentKbpsInterval = undefined;
        this.estimatedKbpsInterval = undefined;
        this.destroyed = false;
        this.spinTimeout = undefined;
        this.ctx = ctx;
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`AbrController Class Context Cancelled: ${reason}`));
        this.addInnerDisposer(this.destroy);
        if (!adapter_1.device.isImplements(adapter_1.Feature.DEBUGGING) ||
            !adapter_1.device.isImplements(adapter_1.Feature.MEDIA_SOURCE) ||
            !adapter_1.device.isImplements(adapter_1.Feature.LOCAL_STORAGE)) {
            throw new Error("Device is not supported");
        }
        this.logger = new log_client_1.LoggerCore(common_1.PACKAGE_NAME).extend(ctx.logger);
        this.device = adapter_1.device;
        this.options = options;
        this.player = player;
        this.driver = driver;
        this.switchImmediately = switchImmediately;
        this.profile = (_a = profileTypes[profile]) !== null && _a !== void 0 ? _a : profileTypes.normal;
        this.maxShifts = maxShifts !== null && maxShifts !== void 0 ? maxShifts : Infinity;
        this.currentKbps = (_b = this.options.bitrate) !== null && _b !== void 0 ? _b : Math.max((_c = this.options.estimatedKbps) !== null && _c !== void 0 ? _c : 0, 244);
        this.lastMessageTime = this.device.performance.now();
        this.lastSwitch = -1 * BASE_SWITCH_THRESHOLD;
        this.init();
    }
    init() {
        this.player.on("fragment", this.onMessage);
        this.profileInterval = adapter_1.device.setInterval(this.profileStream.bind(this), PROFILE_INTERVAL);
        this.trackRecentKbpsInterval = adapter_1.device.setInterval(() => {
            let total = 0;
            for (let i = 0; i < this.rawKbps.length; i++) {
                total += this.rawKbps[i];
            }
            const sample = total / this.rawKbps.length;
            this.rawKbps = [];
            this.recentKbps.unshift(sample);
            this.recentKbps.splice(RECENT_KBPS);
        }, RECENT_KBPS_INTERVAL);
        this.estimatedKbpsInterval = adapter_1.device.setInterval(() => {
            this.emit("profile", {
                estimatedKbps: this.currentKbps,
                profile: this.profile,
            });
            if (this.selectedEncoding == null) {
                this.logger.debug("meow abr estimated", {
                    KBPS: this.currentKbps,
                    modifier: this.emergencyModifier,
                    lastSwitch: this.lastSwitch,
                    shiftable: this.shiftable,
                    profile: this.profile.key,
                });
                return;
            }
            this.logger.debug("meow abr estimated", {
                KBPS: this.currentKbps,
                modifier: this.emergencyModifier,
                lastSwitch: this.lastSwitch,
                shiftable: this.shiftable,
                profile: this.profile.key,
                encoding: this.selectedEncoding,
            });
        }, 4000);
    }
    get shiftable() {
        return (this.totalShifts < this.maxShifts &&
            this.sampleBws.length > MIN_SAMPLE_SIZE &&
            this.device.performance.now() - this.lastSwitch > this.switchThreshold);
    }
    handleEmergencyDrop(modifier, remainingSample) {
        this.dropCount++;
        if (this.dropCount < 2) {
            return;
        }
        this.dropCount = 0;
        this.emergencyModifier = modifier;
        this.sampleBws.splice(remainingSample);
        adapter_1.device.clearTimeout(this.releaseEmergencyTimeout);
        adapter_1.device.setTimeout(() => {
            this.emergencyModifier = 1;
        }, EMERGENCY_RELEASE);
    }
    profileStream() {
        const sortedKbps = [];
        sortedKbps.push(...this.recentKbps);
        sortedKbps.sort((a, b) => {
            if (a > b) {
                return 1;
            }
            return -1;
        });
        const quartile = Math.floor(sortedKbps.length / 4);
        const percentDiff1 = (sortedKbps[quartile * 2] - sortedKbps[quartile]) / sortedKbps[quartile * 2];
        const percentDiff2 = (sortedKbps[quartile * 3] - sortedKbps[quartile * 2]) / sortedKbps[quartile * 3];
        const sum = percentDiff1 + percentDiff2;
        if (sum > 1) {
            this.profile = profileTypes.sporadic;
        }
        else if (sum > 0.65) {
            this.profile = profileTypes.uncertain;
        }
        else if (sum > 0.3) {
            this.profile = profileTypes.normal;
        }
        else {
            this.profile = profileTypes.stable;
        }
    }
    onMessage(ev) {
        if (this.destroyed) {
            return;
        }
        this.lastMessageTime = this.device.performance.now();
        if (ev.time > ev.duration * 2.5) {
            this.dropCount++;
            this.handleEmergencyDrop(0.6, (MIN_SAMPLE_SIZE - 1) * 3);
        }
        else if (ev.time > ev.duration * 2.25) {
            this.handleEmergencyDrop(0.7, Math.max(MIN_SAMPLE_SIZE - 1, Math.floor(SAMPLE_SIZE * 0.5)));
        }
        else if (ev.time > ev.duration * 2) {
            this.handleEmergencyDrop(0.8, Math.max(MIN_SAMPLE_SIZE - 1, Math.floor(SAMPLE_SIZE * 0.7)));
        }
        else if (ev.time > ev.duration * 1.75) {
            this.handleEmergencyDrop(0.9, Math.max(MIN_SAMPLE_SIZE - 1, Math.floor(SAMPLE_SIZE * 0.8)));
        }
        else {
            this.dropCount = 0;
        }
        const bw = (this.profile.bwModifier(ev.size * 8) * this.emergencyModifier) / ev.time;
        this.rawKbps.push((ev.size * 8) / ev.time);
        if (ev.time < MIN_MS_TIME) {
            ev.time = MIN_MS_TIME;
        }
        // Init
        if (ev.size < MIN_SIZE) {
            if (this.count > IGNORE) {
                this.count = Math.floor(IGNORE / 2);
            }
            return;
        }
        // if (this.currentKbps && bw > this.currentKbps * 6 && this.sampleBws.length * 2 < MIN_SIZE) {
        // 	bw = this.currentKbps * 6;
        // }
        this.count++;
        if (!Number.isNaN(bw) && this.count > IGNORE && bw < MAX_BW_ESTIMATE) {
            this.sampleBws.unshift(bw);
            this.sampleBws.splice(SAMPLE_SIZE);
            this.currentKbps = this.calculateCurrentKbps();
        }
        if (this.shiftable) {
            this.shiftIfNecessary();
        }
    }
    shiftIfNecessary() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        if (!(this.driver instanceof mp4_ws_1.Mp4WsPlayer)) {
            // @todo requires to re-implementation in new player
            return;
        }
        if (this.driver.currentLevel == null || adapter_1.device.isSafari) {
            return;
        }
        const { activeLayer } = this.driver;
        const encodings = this.driver.data.encodings.filter((e) => {
            var _a, _b, _c;
            if (activeLayer == null) {
                return true;
            }
            return ((_a = e.videoKbps) !== null && _a !== void 0 ? _a : 0) + ((_b = e.audioKbps) !== null && _b !== void 0 ? _b : 0) < ((_c = activeLayer.bitrate) !== null && _c !== void 0 ? _c : 0) + DELTA;
        });
        const currentEncoding = encodings[this.driver.currentLevel];
        let suggestedEncoding = encodings[0];
        let chosenI = 0;
        for (let i = 0; i < encodings.length; i++) {
            if (((_a = encodings[i].videoKbps) !== null && _a !== void 0 ? _a : 0) + ((_b = encodings[i].audioKbps) !== null && _b !== void 0 ? _b : 0) < this.currentKbps) {
                suggestedEncoding = encodings[i];
                chosenI = i;
            }
            else {
                break;
            }
        }
        if (this.switchImmediately) {
            this.switchBitrate(suggestedEncoding);
            this.switchImmediately = false;
            this.logger.debug("meowpick first adaptive encoding", { suggestedEncoding });
            return;
        }
        if (currentEncoding == null || currentEncoding.videoKbps === suggestedEncoding.videoKbps) {
            return;
        }
        suggestedEncoding.kbps = ((_c = suggestedEncoding.videoKbps) !== null && _c !== void 0 ? _c : 0) + ((_d = suggestedEncoding.audioKbps) !== null && _d !== void 0 ? _d : 0);
        currentEncoding.kbps = ((_e = currentEncoding.videoKbps) !== null && _e !== void 0 ? _e : 0) + ((_f = currentEncoding.audioKbps) !== null && _f !== void 0 ? _f : 0);
        // Stiffness criteria
        if (suggestedEncoding.kbps > currentEncoding.kbps) {
            while (this.profile.resistUpshift(this.currentKbps, (_g = suggestedEncoding.kbps) !== null && _g !== void 0 ? _g : 0) &&
                ((_h = suggestedEncoding.kbps) !== null && _h !== void 0 ? _h : 0) > currentEncoding.kbps) {
                suggestedEncoding = encodings[--chosenI];
                if (suggestedEncoding == null || ((_j = suggestedEncoding.kbps) !== null && _j !== void 0 ? _j : 0) < currentEncoding.kbps) {
                    //this.logger.debug("meow tried to upshift - too stiff");
                    return;
                }
            }
            this.emit("upshift");
        }
        else {
            while (this.profile.resistDownshift(this.currentKbps, (_k = suggestedEncoding.kbps) !== null && _k !== void 0 ? _k : 0) &&
                ((_l = suggestedEncoding.kbps) !== null && _l !== void 0 ? _l : 0) < currentEncoding.kbps) {
                suggestedEncoding = encodings[++chosenI];
                if (suggestedEncoding == null || ((_m = suggestedEncoding.kbps) !== null && _m !== void 0 ? _m : 0) > currentEncoding.kbps) {
                    //this.logger.debug("meow tried to downshift - too stiff");
                    return;
                }
            }
            this.emit("downshift");
        }
        if (currentEncoding.videoKbps === suggestedEncoding.videoKbps) {
            return;
        }
        this.logger.debug("meow switching bitrate", {
            currentEncoding,
            suggestedEncoding: currentEncoding,
            currentKbps: this.currentKbps,
        });
        this.lastSwitch = this.device.performance.now();
        this.selectedEncoding = suggestedEncoding;
        this.switchBitrate(suggestedEncoding);
        this.totalShifts++;
    }
    switchBitrate(suggestedEncoding) {
        if (!(this.driver instanceof mp4_ws_1.Mp4WsPlayer)) {
            // @todo requires to re-implementation in new player
            return;
        }
        this.emit("meow profile", {
            estimatedKbps: this.currentKbps,
            profile: this.profile,
        });
        this.driver.setEncoding(suggestedEncoding);
    }
    calculateCurrentKbps() {
        let total = 0;
        for (let i = 0; i < this.sampleBws.length; i++) {
            total += this.sampleBws[i];
        }
        return total / this.sampleBws.length;
    }
    destroy() {
        var _a;
        adapter_1.device.clearTimeout(this.spinTimeout);
        adapter_1.device.clearTimeout(this.releaseEmergencyTimeout);
        clearInterval(this.estimatedKbpsInterval);
        this.destroyed = true;
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.removeListener("fragment", this.onMessage);
    }
}
AbrController.displayName = "AbrController";
//# sourceMappingURL=player.js.map